import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ClipboardList,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  PackagePlus,
  Upload,
  X,
} from 'lucide-react';
import apiClient from '../../api/client';
import { downloadStockTemplate } from './downloadTemplate';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/ConfirmDialog';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { cn } from '../../utils/cn';
import { ColumnMappingStep, mappingProblems } from './ColumnMappingStep';
import { recallMapping, rememberMapping } from './columnMemory';
import { ResolveLine } from './ResolveLine';
import { PurchaseBar, matchSupplier, type PurchaseDraft, type SupplierOption } from './PurchaseBar';
import { useNavigate } from 'react-router-dom';
import { useCapabilitiesStore } from '../../stores/capabilitiesStore';
import { Permission } from '../../auth/permissions';
import {
  STOCK_FIELDS,
  emptyMapping,
  type AnalyzeResponse,
  type InvoiceJob,
  type InvoiceSummary,
  type Mapping,
  type MappingSource,
  type RowResolution,
  type StockField,
  type StockImportMode,
  type StockImportResult,
  type StockPreviewResponse,
  type StockPreviewRow,
  type StockRowStatus,
} from './types';

type Step = 'upload' | 'map' | 'review' | 'done';

const ACCEPT =
  '.csv,.txt,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pdf,application/pdf';

/** The warehouse's stock import takes spreadsheets only; invoice PDFs belong to Purchases. */
const ACCEPT_SHEETS =
  '.csv,.txt,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const EMPTY_PURCHASE: PurchaseDraft = { supplierId: '', documentNumber: '', documentDate: '' };

const isPdfFile = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);

/** Claude reads a long invoice in one to a few minutes; give up well past that. */
const INVOICE_POLL_MS = 3000;
const INVOICE_GIVE_UP_MS = 15 * 60_000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const actionable = (s: StockRowStatus) =>
  s === 'RECEIPT' || s === 'ADJUST' || s === 'CREATE' || s === 'INVOICE_ONLY';

/** Suggested mapping from the server, overlaid with what this browser remembers from past imports. */
function initialMapping(a: AnalyzeResponse): {
  mapping: Mapping;
  sources: Partial<Record<StockField, MappingSource>>;
} {
  const mapping = emptyMapping();
  const sources: Partial<Record<StockField, MappingSource>> = {};
  for (const f of STOCK_FIELDS) {
    const s = a.suggestions[f];
    if (s) {
      mapping[f] = s.column;
      sources[f] = s.reason;
    }
  }
  for (const [field, col] of Object.entries(recallMapping(a.columns)) as [StockField, number][]) {
    for (const other of STOCK_FIELDS) {
      if (other !== field && mapping[other] === col) {
        mapping[other] = null;
        delete sources[other];
      }
    }
    if (mapping[field] !== col) {
      mapping[field] = col;
      sources[field] = 'REMEMBERED';
    }
  }
  return { mapping, sources };
}

/**
 * Stock import wizard — Purchasing & Stock Management. Files differ every time, so instead of
 * demanding fixed headers: (1) pick the movement type and drop the file, (2) check the suggested
 * column mapping and correct it, (3) review every row's verdict, then post. Nothing is written
 * before the final confirmation; each posted row is one immutable ledger movement.
 */
/**
 * `purpose`:
 *   STOCK    — Warehouse → "Import de stock": opening stock and inventory counts (receipt or count),
 *              spreadsheets only, nothing recorded beyond the ledger.
 *   PURCHASE — Purchases → "Import facture d'achat" (2026-10-13): a no-API supplier's invoice
 *              (PDF or spreadsheet), received only, recorded as that supplier's purchase.
 */
export function StockImportWizard({
  open,
  onClose,
  purpose = 'STOCK',
}: {
  open: boolean;
  onClose: () => void;
  purpose?: 'STOCK' | 'PURCHASE';
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPurchase = purpose === 'PURCHASE';
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [purchase, setPurchase] = useState<PurchaseDraft>(EMPTY_PURCHASE);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const { push, showApiError } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [step, setStep] = useState<Step>('upload');
  const [mode, setMode] = useState<StockImportMode>('RECEIPT');
  const [file, setFile] = useState<File | null>(null);
  const [fileBase64, setFileBase64] = useState('');
  const [analysis, setAnalysis] = useState<AnalyzeResponse | null>(null);
  const [mapping, setMapping] = useState<Mapping>(emptyMapping());
  const [sources, setSources] = useState<Partial<Record<StockField, MappingSource>>>({});
  const [preview, setPreview] = useState<StockPreviewResponse | null>(null);
  /** Step 3 decisions for unresolved lines, keyed by file line number. */
  const [resolutions, setResolutions] = useState<Record<string, RowResolution>>({});
  const perms = useCapabilitiesStore((s) => s.permissionSet);
  // Same rule as the server: creating a product sets its cost.
  const canCreate = perms.has(Permission.CATALOG_WRITE) || perms.has(Permission.PRODUCT_COST_WRITE);
  // A purchase price > 5 % off the order / current cost moves the cost past the guard (migration 147).
  const canAcceptPrices = perms.has(Permission.PRODUCT_COST_WRITE);
  const [acceptedPrices, setAcceptedPrices] = useState<number[]>([]);
  const [commandId, setCommandId] = useState('');
  const [result, setResult] = useState<StockImportResult | null>(null);
  const [busy, setBusy] = useState<'invoice' | 'analyze' | 'preview' | 'commit' | null>(null);
  const [invoice, setInvoice] = useState<InvoiceSummary | null>(null);
  // Bumped on reset/close so a poll still running for a discarded invoice stops quietly.
  const invoiceRun = useRef(0);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setPurchase(EMPTY_PURCHASE);
    setAllowDuplicate(false);
    setAcceptedPrices([]);
    setStep('upload');
    setFile(null);
    setFileBase64('');
    setAnalysis(null);
    setMapping(emptyMapping());
    setSources({});
    setPreview(null);
    setResolutions({});
    setResult(null);
    setBusy(null);
    setInvoice(null);
    invoiceRun.current++;
  };

  useEffect(() => {
    if (!open || !isPurchase) return;
    apiClient
      .get<SupplierOption[]>('/purchasing/stock-import/suppliers')
      .then((r) => setSuppliers(r.data))
      .catch(() => setSuppliers([]));
  }, [open, isPurchase]);

  // The supplier printed on the invoice, once both it and the supplier list are known.
  useEffect(() => {
    if (!isPurchase || !invoice || purchase.supplierId) return;
    const m = matchSupplier(invoice.supplierName, suppliers);
    if (m) setPurchase((p) => (p.supplierId ? p : { ...p, supplierId: m.id }));
  }, [isPurchase, invoice, suppliers, purchase.supplierId]);

  // Escape closes only where nothing would be lost.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (step === 'upload' || step === 'done') && !busy) {
        reset();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!open) return null;

  const close = async () => {
    if (step === 'map' || step === 'review') {
      const ok = await confirm({
        title: t('import.stock.wizard.confirmDiscard'),
        description: t('import.stock.wizard.confirmDiscardHint'),
        confirmLabel: t('import.stock.wizard.discard'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    reset();
    onClose();
  };

  // The routers sit behind the idempotency guard: every POST needs an X-Command-Id.
  const post = <T,>(url: string, body: unknown, commandIdHeader: string = crypto.randomUUID()) =>
    apiClient
      .post<T>(url, body, { headers: { 'X-Command-Id': commandIdHeader } })
      .then((r) => r.data);

  const analyze = async (b64: string, opts: { sheet?: string | null; headerRow?: number } = {}) => {
    setBusy('analyze');
    try {
      const a = await post<AnalyzeResponse>('/purchasing/stock-import/analyze', {
        fileBase64: b64,
        ...opts,
      });
      const init = initialMapping(a);
      setAnalysis(a);
      setMapping(init.mapping);
      setSources(init.sources);
      setPreview(null);
      setResolutions({});
      setStep('map');
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  const downloadTemplate = async () => {
    try {
      await downloadStockTemplate();
    } catch (err) {
      showApiError(err);
    }
  };

  /**
   * An invoice PDF is read by Claude in the background; the lines come back as a CSV, which then
   * goes through the same column check, preview and commit as an uploaded spreadsheet.
   */
  const readInvoice = async (f: File) => {
    const run = ++invoiceRun.current;
    setBusy('invoice');
    try {
      const pdf = await fileToBase64(f);
      const { id } = await post<{ id: string }>('/purchasing/stock-import/invoice', {
        fileBase64: pdf,
        filename: f.name,
      });
      const started = Date.now();
      let job: InvoiceJob;
      do {
        await sleep(INVOICE_POLL_MS);
        if (run !== invoiceRun.current) return;
        if (Date.now() - started > INVOICE_GIVE_UP_MS)
          throw new Error(t('import.stock.wizard.invoiceTimeout'));
        job = (await apiClient.get<InvoiceJob>(`/purchasing/stock-import/invoice/${id}`)).data;
      } while (job.status === 'PROCESSING');
      if (run !== invoiceRun.current) return;
      if (job.status === 'FAILED') {
        push({ tone: 'error', title: t('import.stock.wizard.invoiceFailed'), message: job.error });
        setBusy(null);
        return;
      }
      setInvoice(job.invoice);
      setPurchase((p) => ({
        ...p,
        documentNumber: job.invoice.documentNumber ?? '',
        documentDate: job.invoice.documentDate ?? '',
      }));
      setFile(new File([], job.file.filename, { type: 'text/csv' }));
      setFileBase64(job.file.fileBase64);
      // Goods on an invoice are received, never counted.
      setMode('RECEIPT');
      await analyze(job.file.fileBase64);
    } catch (err) {
      if (run === invoiceRun.current) {
        showApiError(err);
        setBusy(null);
      }
    }
  };

  const pickFile = async (f: File | null) => {
    if (!f) return;
    setInvoice(null);
    if (isPdfFile(f) && !isPurchase) {
      push({ tone: 'warning', message: t('import.purchase.pdfBelongsToPurchases') });
      return;
    }
    if (isPdfFile(f)) {
      setFile(f);
      await readInvoice(f);
      return;
    }
    setFile(f);
    try {
      const b64 = await fileToBase64(f);
      setFileBase64(b64);
      await analyze(b64);
    } catch (err) {
      showApiError(err);
    }
  };

  const assign = (field: StockField, column: number | null) => {
    setMapping((m) => {
      const next = { ...m };
      if (column != null) {
        for (const f of STOCK_FIELDS) if (f !== field && next[f] === column) next[f] = null;
      }
      next[field] = column;
      return next;
    });
    setSources((s) => {
      const next = { ...s };
      if (column != null) {
        for (const f of STOCK_FIELDS) if (f !== field && mapping[f] === column) delete next[f];
        next[field] = 'MANUAL';
      } else {
        delete next[field];
      }
      return next;
    });
    setPreview(null);
  };

  const layout = (res: Record<string, RowResolution> = resolutions) => ({
    sheet: analysis?.sheet ?? undefined,
    headerRow: analysis?.headerRow,
    mapping,
    resolutions: res,
  });

  /** The purchase this receipt records — only once a supplier is chosen. */
  const purchaseBody = (p: PurchaseDraft = purchase, dup: boolean = allowDuplicate) =>
    isPurchase && p.supplierId
      ? {
          supplierId: p.supplierId,
          documentNumber: p.documentNumber.trim() || null,
          documentDate: p.documentDate || null,
          allowDuplicate: dup,
          acceptPriceLines: acceptedPrices,
        }
      : undefined;

  const runPreview = async (
    res: Record<string, RowResolution> = resolutions,
    draft: PurchaseDraft = purchase
  ) => {
    setBusy('preview');
    try {
      const p = await post<StockPreviewResponse>('/purchasing/stock-import/preview', {
        movementType: mode,
        fileBase64,
        filename: file?.name,
        ...layout(res),
        purchase: purchaseBody(draft),
      });
      setPreview(p);
      // One command id per reviewed preview: a double click or a retry after a network blip
      // replays the same commit instead of posting every movement twice.
      setCommandId(crypto.randomUUID());
      setStep('review');
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  /** A step-3 decision (null = undo it): the server re-evaluates the file with it. */
  const resolveLine = (line: number, decision: RowResolution | null) => {
    const next = { ...resolutions };
    if (decision) next[String(line)] = decision;
    else delete next[String(line)];
    setResolutions(next);
    void runPreview(next);
  };

  /** Several step-3 decisions at once (bulk "create from TecDoc"), then one re-evaluation. */
  const resolveLines = (decisions: Record<number, RowResolution>) => {
    const next = { ...resolutions };
    for (const [line, d] of Object.entries(decisions)) next[line] = d;
    setResolutions(next);
    void runPreview(next);
  };

  const runCommit = async () => {
    setBusy('commit');
    try {
      const r = await post<StockImportResult>(
        '/purchasing/stock-import/commit',
        {
          movementType: mode,
          fileBase64,
          filename: file?.name,
          ...layout(),
          purchase: purchaseBody(),
        },
        commandId
      );
      if (analysis) rememberMapping(analysis.columns, mapping);
      setResult(r);
      setStep('done');
      push({
        tone: r.failed > 0 ? 'warning' : 'success',
        title: t('import.stock.processed', { count: r.total }),
        message: t('import.stock.posted', { count: r.applied }) + '.',
      });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  const problems = mappingProblems(mapping);
  const steps: { key: Step; label: string }[] = [
    { key: 'upload', label: t('import.stock.wizard.stepFile') },
    { key: 'map', label: t('import.stock.wizard.stepMap') },
    { key: 'review', label: t('import.stock.wizard.stepReview') },
  ];
  const stepIndex = step === 'done' ? 3 : steps.findIndex((s) => s.key === step);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => (step === 'upload' || step === 'done') && !busy && void close()}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stock-import-title"
        className={cn(
          'relative flex max-h-[94vh] w-full flex-col rounded-lg bg-background shadow-xl transition-[max-width]',
          step === 'map' || step === 'review' ? 'max-w-7xl' : 'max-w-2xl'
        )}
      >
        <header className="flex items-center justify-between gap-4 border-b px-5 py-3">
          <div className="min-w-0">
            <h2 id="stock-import-title" className="text-lg font-semibold">
              {t(isPurchase ? 'import.purchase.dialogTitle' : 'import.stock.dialogTitle')}
            </h2>
            <ol className="mt-1 flex flex-wrap items-center gap-1 text-xs">
              {steps.map((s, i) => (
                <li key={s.key} className="flex items-center gap-1">
                  {i > 0 && <span className="mx-1 h-px w-4 bg-border" aria-hidden />}
                  <span
                    className={cn(
                      'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold',
                      i < stepIndex
                        ? 'bg-primary text-primary-foreground'
                        : i === stepIndex
                          ? 'bg-primary/15 text-primary ring-1 ring-primary'
                          : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {i < stepIndex ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  <span
                    className={cn(
                      i === stepIndex ? 'font-medium text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {s.label}
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <button
            onClick={() => void close()}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label={t('import.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {step === 'upload' && (
            <div className="space-y-5">
              {isPurchase && (
                <p className="text-sm text-muted-foreground">{t('import.purchase.uploadHint')}</p>
              )}
              <fieldset className={cn(isPurchase && 'hidden')}>
                <legend className="mb-2 text-sm font-medium">
                  {t('import.stock.wizard.whatIsFile')}
                </legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(['RECEIPT', 'INVENTORY'] as const).map((m) => (
                    <label
                      key={m}
                      className={cn(
                        'flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors',
                        mode === m
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'hover:bg-accent/40'
                      )}
                    >
                      <input
                        type="radio"
                        name="stock-import-mode"
                        className="sr-only"
                        checked={mode === m}
                        onChange={() => setMode(m)}
                      />
                      {m === 'RECEIPT' ? (
                        <PackagePlus
                          className={cn(
                            'mt-0.5 h-5 w-5 shrink-0',
                            mode === m ? 'text-primary' : 'text-muted-foreground'
                          )}
                        />
                      ) : (
                        <ClipboardList
                          className={cn(
                            'mt-0.5 h-5 w-5 shrink-0',
                            mode === m ? 'text-primary' : 'text-muted-foreground'
                          )}
                        />
                      )}
                      <span>
                        <span className="block text-sm font-medium">
                          {t(m === 'RECEIPT' ? 'import.stock.receipt' : 'import.stock.inventory')}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {t(
                            m === 'RECEIPT'
                              ? 'import.stock.receiptHint'
                              : 'import.stock.inventoryHint'
                          )}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  void pickFile(e.dataTransfer.files?.[0] ?? null);
                }}
                onClick={() => !busy && inputRef.current?.click()}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
                role="button"
                tabIndex={0}
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-10 text-center transition-colors',
                  dragging ? 'border-primary bg-primary/5' : 'border-input hover:bg-accent/40'
                )}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept={isPurchase ? ACCEPT : ACCEPT_SHEETS}
                  className="hidden"
                  onChange={(e) => {
                    void pickFile(e.target.files?.[0] ?? null);
                    e.target.value = '';
                  }}
                />
                {busy === 'invoice' ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="text-sm font-medium">
                      {t('import.stock.wizard.readingInvoice', { name: file?.name })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('import.stock.wizard.readingInvoiceHint')}
                    </span>
                  </>
                ) : busy === 'analyze' ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="text-sm font-medium">
                      {t('import.stock.wizard.reading', { name: file?.name })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('import.stock.wizard.readingHint')}
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('import.dropHint')}</span>
                    <span className="text-xs text-muted-foreground">
                      {t(
                        isPurchase
                          ? 'import.stock.wizard.dropFormats'
                          : 'import.stock.wizard.dropFormatsSheets'
                      )}
                    </span>
                  </>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2">
                <p className="text-xs text-muted-foreground">
                  {t('import.stock.wizard.anyLayout')}
                </p>
                <Button variant="outline" size="sm" onClick={() => void downloadTemplate()}>
                  <Download className="h-4 w-4" /> {t('import.stock.wizard.template')}
                </Button>
              </div>
            </div>
          )}

          {invoice && (step === 'map' || step === 'review') && <InvoiceBanner invoice={invoice} />}

          {isPurchase && (step === 'map' || step === 'review') && (
            <PurchaseBar
              suppliers={suppliers}
              printedSupplier={invoice?.supplierName ?? null}
              value={purchase}
              disabled={busy !== null}
              onChange={setPurchase}
              onCommitField={(next) => {
                // In review, re-ask the server: is this invoice already in the register?
                if (step === 'review') void runPreview(resolutions, next);
              }}
              onSupplierCreated={(s) =>
                setSuppliers((list) => [...list, s].sort((a, b) => a.name.localeCompare(b.name)))
              }
            />
          )}

          {isPurchase && step === 'review' && preview?.alreadyRecorded && (
            <div className="mb-4 rounded-lg border border-warning/50 bg-warning/10 px-3 py-2 text-sm">
              <p className="font-medium">
                {t('import.purchase.alreadyRecorded', {
                  number: preview.alreadyRecorded.invoiceNumber,
                  date: preview.alreadyRecorded.createdAt.slice(0, 10),
                  count: preview.alreadyRecorded.lineCount,
                })}
              </p>
              <label className="mt-1 flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={allowDuplicate}
                  onChange={(e) => setAllowDuplicate(e.target.checked)}
                />
                {t('import.purchase.receiveAgain')}
              </label>
            </div>
          )}

          {step === 'map' && analysis && (
            <ColumnMappingStep
              mode={mode}
              fileName={file?.name ?? ''}
              analysis={analysis}
              mapping={mapping}
              sources={sources}
              busy={busy !== null}
              onAssign={assign}
              onSheet={(sheet) => void analyze(fileBase64, { sheet })}
              onHeaderRow={(headerRow) =>
                void analyze(fileBase64, { sheet: analysis.sheet, headerRow })
              }
              onReset={() => {
                const init = initialMapping(analysis);
                setMapping(init.mapping);
                setSources(init.sources);
              }}
              onChangeFile={reset}
              lockMode={isPurchase}
              onMode={(m) => {
                setMode(m);
                setPreview(null);
              }}
            />
          )}

          {step === 'review' && preview && (
            <ReviewStep
              preview={preview}
              mode={mode}
              canCreate={canCreate}
              canAcceptPrices={canAcceptPrices}
              acceptedPrices={acceptedPrices}
              onTogglePrice={(line) =>
                setAcceptedPrices((cur) =>
                  cur.includes(line) ? cur.filter((x) => x !== line) : [...cur, line]
                )
              }
              busy={busy !== null}
              onResolve={resolveLine}
              onResolveMany={resolveLines}
              countLike={
                !isPurchase && mode === 'RECEIPT' && analysis?.suggestedMode === 'INVENTORY'
              }
              onSwitchToCount={() => {
                setMode('INVENTORY');
                setPreview(null);
                setStep('map');
              }}
            />
          )}

          {step === 'done' && result && <DoneStep result={result} />}
          {step === 'done' && result?.purchaseInvoiceId && (
            <div className="mt-3 text-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  reset();
                  onClose();
                  navigate(`/purchasing?invoice=${result.purchaseInvoiceId}`);
                }}
              >
                {t('import.purchase.openRecord')}
              </Button>
            </div>
          )}
        </div>

        {step === 'map' && (
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
            <span
              className={cn(
                'text-xs',
                problems.length ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {problems.length
                ? problems.map((p) => t(`import.stock.map.problem_${p}`)).join(' ')
                : t('import.stock.map.readyHint', { count: analysis?.totalRows ?? 0 })}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset} disabled={busy !== null}>
                <ArrowLeft className="h-4 w-4" /> {t('import.back')}
              </Button>
              <Button
                size="sm"
                onClick={() => void runPreview()}
                disabled={busy !== null || problems.length > 0}
              >
                {busy === 'preview' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {busy === 'preview' ? t('import.validating') : t('import.stock.map.toReview')}
              </Button>
            </div>
          </footer>
        )}

        {step === 'review' && preview && (
          <ReviewFooter
            preview={preview}
            mode={mode}
            busy={busy}
            blocked={
              isPurchase && !purchase.supplierId
                ? t('import.purchase.needSupplier')
                : isPurchase && preview.alreadyRecorded && !allowDuplicate
                  ? t('import.purchase.needDuplicateConfirm')
                  : isPurchase &&
                      (preview.priceVariances ?? []).some((l) => !acceptedPrices.includes(l))
                    ? t(
                        canAcceptPrices
                          ? 'import.purchase.price.needAccept'
                          : 'import.purchase.price.needOwner',
                        {
                          count: (preview.priceVariances ?? []).filter(
                            (l) => !acceptedPrices.includes(l)
                          ).length,
                        }
                      )
                    : null
            }
            onBack={() => setStep('map')}
            onCommit={() => void runCommit()}
          />
        )}

        {confirmNode}

        {step === 'done' && (
          <footer className="flex justify-end gap-2 border-t px-5 py-3">
            <Button variant="outline" size="sm" onClick={reset}>
              <Upload className="h-4 w-4" /> {t('import.stock.wizard.another')}
            </Button>
            <Button size="sm" onClick={() => void close()}>
              {t('import.close')}
            </Button>
          </footer>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

type Filter = 'ALL' | 'APPLY' | 'CREATE' | 'NOT_FOUND' | 'ERROR' | 'NO_CHANGE';

const statusVariant = (s: StockRowStatus) =>
  actionable(s)
    ? 'success'
    : s === 'ERROR'
      ? 'destructive'
      : s === 'NOT_FOUND'
        ? 'warning'
        : 'muted';

function ReviewStep({
  preview,
  mode,
  canCreate,
  canAcceptPrices = false,
  acceptedPrices = [],
  onTogglePrice,
  busy,
  onResolve,
  onResolveMany,
  countLike = false,
  onSwitchToCount,
}: {
  preview: StockPreviewResponse;
  mode: StockImportMode;
  canCreate: boolean;
  canAcceptPrices?: boolean;
  acceptedPrices?: number[];
  onTogglePrice?: (line: number) => void;
  busy: boolean;
  onResolve: (line: number, decision: RowResolution | null) => void;
  onResolveMany?: (decisions: Record<number, RowResolution>) => void;
  /** A stock-count file (a "Réel" column) about to be RECEIVED: its quantities would be ADDED. */
  countLike?: boolean;
  onSwitchToCount?: () => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('ALL');
  // Bulk "create from TecDoc": lines TecDoc knows under exactly one article (the operator still
  // chooses which — a different brand than the file's is shown on the line, not hidden).
  const tecdocOne = canCreate
    ? preview.rows.filter((r) => r.status === 'NOT_FOUND' && r.tecdoc?.length === 1)
    : [];
  const [picked, setPicked] = useState<number[]>([]);
  useEffect(() => setPicked([]), [preview]);
  const createPicked = () => {
    const decisions: Record<number, RowResolution> = {};
    for (const r of tecdocOne.filter((x) => picked.includes(x.line))) {
      const c = r.tecdoc![0];
      decisions[r.line] = {
        action: 'CREATE',
        name: r.description || r.ref,
        brand: c.brand,
        manufacturerRef: c.articleNr || r.ref,
        purchasePriceHT: r.unitPrice ?? null,
        tecdocArtId: c.artId,
      };
    }
    onResolveMany?.(decisions);
  };
  const counts = useMemo(() => {
    const c = {
      ALL: preview.rows.length,
      APPLY: 0,
      CREATE: 0,
      NOT_FOUND: 0,
      ERROR: 0,
      NO_CHANGE: 0,
    };
    for (const r of preview.rows) {
      if (r.status === 'RECEIPT' || r.status === 'ADJUST' || r.status === 'INVOICE_ONLY') c.APPLY++;
      else c[r.status as 'CREATE' | 'NOT_FOUND' | 'ERROR' | 'NO_CHANGE']++;
    }
    return c;
  }, [preview]);
  const rows = preview.rows.filter((r) =>
    filter === 'ALL'
      ? true
      : filter === 'APPLY'
        ? r.status === 'RECEIPT' || r.status === 'ADJUST' || r.status === 'INVOICE_ONLY'
        : r.status === filter
  );
  const units = preview.rows.reduce(
    (s, r) => s + (actionable(r.status) ? (r.movement ?? 0) : 0),
    0
  );

  const tiles: { key: Filter; label: string; tone: string }[] = [
    { key: 'ALL', label: t('import.stock.review.all'), tone: 'text-foreground' },
    { key: 'APPLY', label: t(`import.stock.review.apply_${mode}`), tone: 'text-success' },
    { key: 'CREATE', label: t('import.stock.review.toCreate'), tone: 'text-primary' },
    { key: 'NOT_FOUND', label: t('import.stock.review.notFound'), tone: 'text-warning' },
    { key: 'ERROR', label: t('import.stock.review.errors'), tone: 'text-destructive' },
    { key: 'NO_CHANGE', label: t('import.stock.review.noChange'), tone: 'text-muted-foreground' },
  ];

  const exportIssues = () => {
    const issues = preview.rows.filter((r) => r.status === 'ERROR' || r.status === 'NOT_FOUND');
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      [
        t('import.stock.review.csv.row'),
        t('import.stock.review.csv.reference'),
        t('import.stock.review.csv.brand'),
        t('import.stock.review.csv.designation'),
        t('import.stock.review.csv.quantity'),
        t('import.stock.review.csv.unitPriceHt'),
        t('import.stock.review.csv.status'),
        t('import.stock.review.csv.message'),
      ].join(','),
      ...issues.map((r) =>
        [
          r.line,
          r.ref,
          r.brand ?? r.fileBrand,
          r.description,
          r.quantity,
          r.unitPrice,
          r.status,
          r.message,
        ]
          .map(esc)
          .join(',')
      ),
    ];
    const blob = new Blob(['\uFEFF' + lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stock-import-issues.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        {tiles.map((tile) => (
          <button
            key={tile.key}
            type="button"
            onClick={() => setFilter(tile.key)}
            disabled={tile.key !== 'ALL' && counts[tile.key] === 0}
            aria-pressed={filter === tile.key}
            className={cn(
              'rounded-lg border px-3 py-2 text-left transition-colors disabled:cursor-default disabled:opacity-50',
              filter === tile.key
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'hover:bg-accent/40'
            )}
          >
            <div className={cn('text-xl font-semibold tabular-nums', tile.tone)}>
              {counts[tile.key]}
            </div>
            <div className="text-xs text-muted-foreground">{tile.label}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {mode === 'RECEIPT'
            ? t('import.stock.review.unitsIn', { count: units })
            : t('import.stock.review.netAdjust', { value: (units > 0 ? '+' : '') + units })}
          {preview.ignoredColumns.length > 0 &&
            ` · ${t('import.ignoredColumns', { columns: preview.ignoredColumns.join(', ') })}`}
        </span>
        {counts.ERROR + counts.NOT_FOUND > 0 && (
          <Button variant="outline" size="sm" onClick={exportIssues}>
            <Download className="h-4 w-4" /> {t('import.stock.review.exportIssues')}
          </Button>
        )}
      </div>

      {countLike && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
          <span className="font-medium text-destructive">
            {t('import.stock.review.countWarning', { count: units })}
          </span>
          <Button size="sm" variant="outline" onClick={onSwitchToCount}>
            {t('import.stock.map.switchTo_INVENTORY')}
          </Button>
        </div>
      )}

      {tecdocOne.length > 0 && onResolveMany && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={picked.length === tecdocOne.length}
              onChange={(e) => setPicked(e.target.checked ? tecdocOne.map((r) => r.line) : [])}
              disabled={busy}
            />
            {t('import.stock.review.tecdocSelectAll', { count: tecdocOne.length })}
          </label>
          <Button size="sm" onClick={createPicked} disabled={busy || picked.length === 0}>
            {t('import.stock.review.tecdocCreate', { count: picked.length })}
          </Button>
        </div>
      )}

      <div className="max-h-[52vh] overflow-auto rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-muted/90 backdrop-blur">
            <tr className="text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">{t('import.stock.review.colRow')}</th>
              <th className="px-3 py-2 font-medium">{t('import.stock.review.colRef')}</th>
              <th className="px-3 py-2 font-medium">{t('import.stock.review.colBrand')}</th>
              <th className="px-3 py-2 font-medium">{t('import.stock.review.colProduct')}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t(`import.stock.fields.quantity_${mode}`)}
              </th>
              <th className="px-3 py-2 font-medium">{t('import.stock.review.colStock')}</th>
              <th className="px-3 py-2 font-medium">{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <ReviewRow
                key={r.line}
                row={r}
                pick={
                  tecdocOne.some((x) => x.line === r.line)
                    ? {
                        on: picked.includes(r.line),
                        toggle: () =>
                          setPicked((cur) =>
                            cur.includes(r.line)
                              ? cur.filter((x) => x !== r.line)
                              : [...cur, r.line]
                          ),
                      }
                    : undefined
                }
                canCreate={canCreate}
                canAcceptPrices={canAcceptPrices}
                priceAccepted={acceptedPrices.includes(r.line)}
                onTogglePrice={onTogglePrice}
                busy={busy}
                onResolve={onResolve}
              />
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-xs text-muted-foreground">
                  {t('import.stock.review.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewRow({
  row,
  pick,
  canCreate,
  canAcceptPrices = false,
  priceAccepted = false,
  onTogglePrice,
  busy,
  onResolve,
}: {
  row: StockPreviewRow;
  /** Bulk "create from TecDoc" selection, on lines TecDoc knows under one article. */
  pick?: { on: boolean; toggle: () => void };
  canCreate: boolean;
  canAcceptPrices?: boolean;
  priceAccepted?: boolean;
  onTogglePrice?: (line: number) => void;
  busy: boolean;
  onResolve: (line: number, decision: RowResolution | null) => void;
}) {
  const { t } = useTranslation();
  const bad = row.status === 'ERROR';
  return (
    <tr
      className={cn(
        'border-t align-top',
        bad && 'bg-destructive/5',
        row.status === 'NOT_FOUND' && 'bg-warning/5',
        row.status === 'CREATE' && 'bg-primary/5'
      )}
    >
      <td className="px-3 py-1.5 text-xs tabular-nums text-muted-foreground">
        <span className="flex items-center gap-1.5">
          {pick && (
            <input
              type="checkbox"
              checked={pick.on}
              onChange={pick.toggle}
              disabled={busy}
              aria-label={`${row.line}`}
            />
          )}
          {row.line}
        </span>
      </td>
      <td className="px-3 py-1.5 font-mono text-xs">
        <div className="max-w-[12rem] truncate" title={row.ref}>
          {row.ref || '—'}
        </div>
        {row.description && (
          <div
            className="max-w-[12rem] truncate font-sans text-muted-foreground"
            title={row.description}
          >
            {row.description}
          </div>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs">
        {row.brand || row.fileBrand ? (
          <span
            title={
              row.fileBrand && row.fileBrand !== row.brand
                ? `${t('import.stock.review.inFile')}: ${row.fileBrand}`
                : undefined
            }
          >
            {row.brand ?? row.fileBrand}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs">
        {row.status === 'CREATE' && row.create ? (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-mono">{row.create.manufacturerRef}</span>
              <Badge variant="outline" className="px-1 py-0 text-[10px]">
                {row.resolvedBy === 'TECDOC'
                  ? t('import.stock.review.newFromTecdoc')
                  : t('import.stock.review.newManual')}
              </Badge>
            </div>
            <div className="max-w-[18rem] truncate text-muted-foreground" title={row.create.name}>
              {row.create.name}
            </div>
          </div>
        ) : row.productSku ? (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-mono">{row.productSku}</span>
              {row.matchedBy && row.matchedBy !== 'SKU' && (
                <Badge variant="outline" className="px-1 py-0 text-[10px]">
                  {t(`import.stock.review.by_${row.matchedBy}`)}
                </Badge>
              )}
            </div>
            <div className="max-w-[18rem] truncate text-muted-foreground" title={row.productName}>
              {row.productName}
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums">
        {row.quantity ?? '—'}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs tabular-nums">
        {row.status === 'INVOICE_ONLY' ? (
          <span className="text-muted-foreground">
            {row.current ?? 0} · {t('import.purchase.match.noMovement')}
          </span>
        ) : row.movement != null ? (
          <span>
            {row.current ?? 0} → <strong>{row.resulting}</strong>{' '}
            <span className={row.movement > 0 ? 'text-success' : 'text-destructive'}>
              ({row.movement > 0 ? '+' : ''}
              {row.movement})
            </span>
          </span>
        ) : row.current != null ? (
          <span className="text-muted-foreground">{row.current}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs">
        <Badge variant={statusVariant(row.status)}>{t(`import.stock.status.${row.status}`)}</Badge>
        {row.message && (
          <div
            className={cn(
              'mt-0.5 max-w-[22rem]',
              bad
                ? 'text-destructive'
                : actionable(row.status)
                  ? 'font-medium text-warning' // e.g. brand in file ≠ brand of the matched product
                  : 'text-muted-foreground'
            )}
          >
            {row.message}
          </div>
        )}
        <ResolveLine row={row} canCreate={canCreate} disabled={busy} onResolve={onResolve} />
        {row.purchase && (
          <PurchaseLineInfo
            row={row}
            canAccept={canAcceptPrices}
            accepted={priceAccepted}
            disabled={busy}
            onToggle={() => onTogglePrice?.(row.line)}
          />
        )}
      </td>
    </tr>
  );
}

const money = (v: number) =>
  v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Purchase invoice: which purchase orders the line settles, and its price against the reference. */
function PurchaseLineInfo({
  row,
  canAccept,
  accepted,
  disabled,
  onToggle,
}: {
  row: StockPreviewRow;
  canAccept: boolean;
  accepted: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const p = row.purchase!;
  const pos = p.poNumbers.join(', ');
  return (
    <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
      {p.alreadyInStock > 0 && (
        <div>{t('import.purchase.match.alreadyInStock', { count: p.alreadyInStock, pos })}</div>
      )}
      {p.receivedOnPo > 0 && (
        <div>{t('import.purchase.match.receivedOnPo', { count: p.receivedOnPo, pos })}</div>
      )}
      {p.free > 0 && (p.alreadyInStock > 0 || p.receivedOnPo > 0) && (
        <div>{t('import.purchase.match.free', { count: p.free })}</div>
      )}
      {row.unitPrice != null && p.costModel === 'TARIFF_DISCOUNT' && (
        <div>{t('import.purchase.price.tariff')}</div>
      )}
      {row.unitPrice != null && p.costModel === 'WAP' && p.referencePrice != null && (
        <div className={cn(p.varianceFlag && 'font-medium text-destructive')}>
          {t(`import.purchase.price.vs_${p.referenceSource ?? 'COST'}`, {
            price: money(row.unitPrice),
            reference: money(p.referencePrice),
            pct: `${(p.variancePct ?? 0) > 0 ? '+' : ''}${p.variancePct ?? 0}`,
          })}
        </div>
      )}
      {p.varianceFlag &&
        (canAccept ? (
          <label className="flex items-center gap-1.5 text-foreground">
            <input type="checkbox" checked={accepted} disabled={disabled} onChange={onToggle} />
            {t('import.purchase.price.accept')}
          </label>
        ) : (
          <div className="text-destructive">{t('import.purchase.price.ownerOnly')}</div>
        ))}
    </div>
  );
}

function ReviewFooter(props: {
  preview: StockPreviewResponse;
  mode: StockImportMode;
  busy: string | null;
  /** Why the commit is not allowed yet (purchase import: no supplier, unconfirmed duplicate). */
  blocked?: string | null;
  onBack: () => void;
  onCommit: () => void;
}) {
  const { t } = useTranslation();
  const apply = props.preview.rows.filter((r) => actionable(r.status)).length;
  const skip = props.preview.rows.length - apply;
  return (
    <footer className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
      <span className={cn('text-xs', props.blocked ? 'text-warning' : 'text-muted-foreground')}>
        {props.blocked && `${props.blocked} `}
        {apply > 0 ? t('import.willApply', { count: apply }) : t('import.stock.review.nothing')}
        {apply > 0 && skip > 0 && ` ${t('import.stock.review.willSkip', { count: skip })}`}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={props.onBack} disabled={props.busy !== null}>
          <ArrowLeft className="h-4 w-4" /> {t('import.stock.review.backToMapping')}
        </Button>
        <Button
          size="sm"
          onClick={props.onCommit}
          disabled={props.busy !== null || apply === 0 || !!props.blocked}
        >
          {props.busy === 'commit' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          {props.busy === 'commit'
            ? t('import.importing')
            : t(`import.stock.review.confirm_${props.mode}`, { count: apply })}
        </Button>
      </div>
    </footer>
  );
}

function DoneStep({ result }: { result: StockImportResult }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 py-2 text-center">
      <CheckCircle2
        className={cn('mx-auto h-10 w-10', result.failed ? 'text-warning' : 'text-success')}
      />
      <div>
        <h3 className="text-base font-semibold">
          {t('import.stock.posted', { count: result.applied })}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('import.stock.processed', { count: result.total })}
          {result.created > 0 && ` · ${t('import.stock.created', { count: result.created })}`}
          {result.skipped > 0 && ` · ${t('import.stock.skipped', { count: result.skipped })}`}
          {result.failed > 0 && ` · ${t('import.stock.failed', { count: result.failed })}`}
        </p>
        {(result.alreadyInStock ?? 0) + (result.costUpdates ?? 0) > 0 && (
          <p className="text-sm text-muted-foreground">
            {(result.alreadyInStock ?? 0) > 0 &&
              t('import.purchase.match.doneAlreadyInStock', { count: result.alreadyInStock })}
            {(result.alreadyInStock ?? 0) > 0 && (result.costUpdates ?? 0) > 0 && ' · '}
            {(result.costUpdates ?? 0) > 0 &&
              t('import.purchase.match.doneCost', { count: result.costUpdates })}
          </p>
        )}
      </div>
      {result.errors.length > 0 && (
        <div className="max-h-56 overflow-auto rounded-lg border text-left">
          <table className="w-full text-xs">
            <tbody>
              {result.errors.map((e) => (
                <tr key={e.row} className="border-t first:border-t-0">
                  <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{e.row}</td>
                  <td className="px-3 py-1.5 font-mono">{e.ref}</td>
                  <td className="px-3 py-1.5 text-destructive">{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <FileSpreadsheet className="h-3.5 w-3.5" /> {t('import.stock.wizard.remembered')}
      </p>
    </div>
  );
}

/** What was read off the PDF — and a plain warning when it is a credit note, not goods in. */
function InvoiceBanner({ invoice }: { invoice: InvoiceSummary }) {
  const { t } = useTranslation();
  const credit = invoice.documentType === 'CREDIT_NOTE' || invoice.creditedLines > 0;
  return (
    <div
      className={cn(
        'mb-4 flex items-start gap-3 rounded-lg border px-3 py-2 text-sm',
        credit ? 'border-warning/40 bg-warning/[.08]' : 'bg-muted/40'
      )}
    >
      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="font-medium">
          {t(`import.stock.wizard.docType.${invoice.documentType}`)}
          {invoice.documentNumber && ` ${invoice.documentNumber}`}
          {invoice.supplierName && ` · ${invoice.supplierName}`}
          {invoice.documentDate && ` · ${invoice.documentDate}`}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('import.stock.wizard.invoiceRead', { count: invoice.lineCount })}
        </p>
        {credit && (
          <p className="mt-1 text-xs text-warning">
            {t('import.stock.wizard.invoiceCredit', { count: invoice.creditedLines })}
          </p>
        )}
      </div>
    </div>
  );
}
